import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Home, User, Eye, EyeOff, Calendar, Clock, 
  Settings, Plus, ChevronRight, Bell, Shield, AlertTriangle,
  Check, X, Image, UserPlus
} from 'lucide-react';
import { InfoTooltip } from '../../components/ExplanationLayers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * PARENT DASHBOARD (PART C — FINAL LOCKED DIRECTIVE)
 * 
 * Required:
 * - Linked children list
 * - Visibility status (ON / OFF / SHARED)
 * - Calendar view (family overlay)
 * - Approvals (photos, deletions if enabled)
 */

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [children, setChildren] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth?role=parent');
      return;
    }
    loadDashboard();
  }, [user, navigate]);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load children
      const childrenRes = await axios.get(`${API}/api/family/children`, { headers });
      setChildren(childrenRes.data.children || []);
      
      // Load pending approvals
      const approvalsRes = await axios.get(`${API}/api/family/approvals`, { headers });
      setPendingApprovals(approvalsRes.data.approvals || []);
      
      // Load upcoming family events
      const eventsRes = await axios.get(`${API}/api/family/calendar/upcoming`, { headers });
      setUpcomingEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateChildVisibility = async (childId, mode) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/family/children/${childId}/visibility`, {
        control_mode: mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setChildren(children.map(c => 
        c.child_id === childId ? { ...c, control_mode: mode } : c
      ));
      toast.success(`Visibility updated to ${mode}`);
    } catch (error) {
      console.error('Failed to update visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handleApproval = async (approvalId, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/family/approvals/${approvalId}`, {
        action: action // 'approve' or 'reject'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPendingApprovals(pendingApprovals.filter(a => a.approval_id !== approvalId));
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
    } catch (error) {
      console.error('Failed to process approval:', error);
      toast.error('Failed to process');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-secondary-orange)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => navigate('/myworld')} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <div className="flex items-center justify-center gap-1">
                <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Family Dashboard</h1>
                <InfoTooltip topic="parent_mode" />
              </div>
            </div>
            <button onClick={() => navigate('/family/settings')} className="p-2 -mr-2 rounded-full hover:bg-black/5">
              <Settings className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        
        {/* Pending Approvals Alert */}
        {pendingApprovals.length > 0 && (
          <Card className="p-4" style={{ backgroundColor: 'rgba(217, 138, 58, 0.1)', border: '1px solid var(--lk-secondary-orange)' }}>
            <div className="flex items-center gap-3 mb-3">
              <Bell className="w-5 h-5" style={{ color: 'var(--lk-secondary-orange)' }} />
              <h2 className="font-semibold" style={{ color: 'var(--lk-secondary-orange)' }}>
                {pendingApprovals.length} Pending Approval{pendingApprovals.length !== 1 ? 's' : ''}
              </h2>
            </div>
            <div className="space-y-2">
              {pendingApprovals.slice(0, 3).map((approval) => (
                <div 
                  key={approval.approval_id}
                  className="p-3 rounded-lg flex items-center gap-3"
                  style={{ backgroundColor: 'var(--fy-surface-card)' }}
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                      {approval.child_name} wants to {approval.type === 'photo' ? 'upload a photo' : approval.description}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                      {new Date(approval.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleApproval(approval.approval_id, 'approve')}
                      className="p-2 rounded-full hover:bg-green-100"
                    >
                      <Check className="w-5 h-5 text-green-600" />
                    </button>
                    <button 
                      onClick={() => handleApproval(approval.approval_id, 'reject')}
                      className="p-2 rounded-full hover:bg-red-100"
                    >
                      <X className="w-5 h-5 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Children Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Your Children</h2>
            <Button 
              onClick={() => navigate('/family/add-child')}
              size="sm"
              className="rounded-full"
              style={{ backgroundColor: 'var(--lk-secondary-orange)', color: 'white' }}
              data-testid="add-child-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Child
            </Button>
          </div>

          {children.length === 0 ? (
            <Card className="p-8 text-center" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <UserPlus className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
              <p className="font-medium mb-1" style={{ color: 'var(--fy-text-secondary)' }}>No children added yet</p>
              <p className="text-sm mb-4" style={{ color: 'var(--fy-text-disabled)' }}>Add your children to manage their accounts</p>
              <Button 
                onClick={() => navigate('/family/add-child')}
                className="rounded-full"
                style={{ backgroundColor: 'var(--lk-secondary-orange)', color: 'white' }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Your First Child
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {children.map((child) => (
                <Card 
                  key={child.child_id}
                  className="p-4"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden"
                      style={{ backgroundColor: 'var(--lk-terra-muted)' }}
                    >
                      {child.picture ? (
                        <img src={child.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-6 h-6" style={{ color: 'var(--lk-secondary-orange)' }} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>{child.name}</p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                        {child.age} years old • {child.account_type === 'child' ? 'Child' : child.account_type === 'teen' ? 'Teen' : 'Youth Adult'}
                      </p>
                    </div>
                    <button 
                      onClick={() => navigate(`/family/child/${child.child_id}`)}
                      className="p-2 rounded-full hover:bg-black/5"
                    >
                      <ChevronRight className="w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
                    </button>
                  </div>
                  
                  {/* Visibility Control */}
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--fy-border-subtle)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Parent Visibility</span>
                        <InfoTooltip topic="parent_mode" />
                      </div>
                      <span 
                        className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                        style={{ 
                          backgroundColor: child.control_mode === 'ON' ? 'var(--lk-sage-muted)' : child.control_mode === 'SHARED' ? 'var(--lk-terra-muted)' : 'var(--fy-surface-muted)',
                          color: child.control_mode === 'ON' ? 'var(--lk-primary-green)' : child.control_mode === 'SHARED' ? 'var(--lk-secondary-orange)' : 'var(--fy-text-secondary)'
                        }}
                      >
                        {child.control_mode === 'ON' ? <Eye className="w-3 h-3" /> : child.control_mode === 'SHARED' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {child.control_mode}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => updateChildVisibility(child.child_id, 'ON')}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium ${child.control_mode === 'ON' ? 'ring-2' : ''}`}
                        style={{ 
                          backgroundColor: child.control_mode === 'ON' ? 'var(--lk-sage-muted)' : 'var(--fy-surface-muted)',
                          color: child.control_mode === 'ON' ? 'var(--lk-primary-green)' : 'var(--fy-text-secondary)',
                          ringColor: 'var(--lk-primary-green)'
                        }}
                      >
                        Full
                      </button>
                      <button 
                        onClick={() => updateChildVisibility(child.child_id, 'SHARED')}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium ${child.control_mode === 'SHARED' ? 'ring-2' : ''}`}
                        style={{ 
                          backgroundColor: child.control_mode === 'SHARED' ? 'var(--lk-terra-muted)' : 'var(--fy-surface-muted)',
                          color: child.control_mode === 'SHARED' ? 'var(--lk-secondary-orange)' : 'var(--fy-text-secondary)',
                          ringColor: 'var(--lk-secondary-orange)'
                        }}
                        disabled={child.account_type === 'child'}
                      >
                        Shared
                      </button>
                      <button 
                        onClick={() => updateChildVisibility(child.child_id, 'OFF')}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium ${child.control_mode === 'OFF' ? 'ring-2' : ''}`}
                        style={{ 
                          backgroundColor: child.control_mode === 'OFF' ? 'var(--fy-surface-card)' : 'var(--fy-surface-muted)',
                          color: 'var(--fy-text-secondary)',
                          ringColor: 'var(--fy-text-secondary)'
                        }}
                        disabled={child.account_type === 'child' || child.account_type === 'teen'}
                      >
                        Off
                      </button>
                    </div>
                    {child.account_type === 'child' && (
                      <p className="text-xs mt-2" style={{ color: 'var(--fy-text-disabled)' }}>
                        <Shield className="w-3 h-3 inline mr-1" />
                        Children under 13 require full visibility
                      </p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Family Calendar Preview */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Family Calendar</h2>
            <Button 
              onClick={() => navigate('/calendar?view=family')}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              View All
            </Button>
          </div>

          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            {upcomingEvents.length === 0 ? (
              <div className="text-center py-6">
                <Calendar className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--fy-text-disabled)' }} />
                <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>No upcoming family events</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.slice(0, 3).map((event, idx) => (
                  <div key={event.event_id || idx} className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'var(--lk-terra-muted)' }}
                    >
                      <Calendar className="w-5 h-5" style={{ color: 'var(--lk-secondary-orange)' }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{event.title}</p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                        {new Date(event.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    {event.child_name && (
                      <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
                        {event.child_name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </section>

        {/* Screen Time Coordination */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Screen Time</h2>
            <Button 
              onClick={() => navigate('/family/screen-time')}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              Manage
            </Button>
          </div>

          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--fy-text-secondary)' }} />
              <div>
                <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                  <strong>Non-enforcing coordination</strong>
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                  Set quiet hours, time warnings, and bonus time. LikeKind shows these to your children but does not restrict access.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* Quick Links */}
        <section>
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <Card 
              className="p-4 cursor-pointer hover:shadow-sm"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
              onClick={() => navigate('/family/add-child')}
            >
              <UserPlus className="w-6 h-6 mb-2" style={{ color: 'var(--lk-secondary-orange)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Add Child</p>
            </Card>
            <Card 
              className="p-4 cursor-pointer hover:shadow-sm"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
              onClick={() => navigate('/calendar?view=family')}
            >
              <Calendar className="w-6 h-6 mb-2" style={{ color: 'var(--lk-secondary-orange)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Family Calendar</p>
            </Card>
            <Card 
              className="p-4 cursor-pointer hover:shadow-sm"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
              onClick={() => navigate('/family/schools')}
            >
              <Home className="w-6 h-6 mb-2" style={{ color: 'var(--lk-earth-brown)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>School Connections</p>
            </Card>
            <Card 
              className="p-4 cursor-pointer hover:shadow-sm"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
              onClick={() => navigate('/faq/families')}
            >
              <Shield className="w-6 h-6 mb-2" style={{ color: 'var(--lk-primary-green)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Safety FAQ</p>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
}
